import React, { useEffect, useRef, useState } from 'react';

import useOrgSettingsStore from '../../stores/orgSettingsStore';
import useAbdmEnabled from '../../hooks/useAbdmEnabled';
import useScanCountersStore from '../../stores/scanCountersStore';
import { SETTINGS_COPY } from '../../constants/abdmCopy';

/**
 * HipSettings — the per-org ABDM HIP (Health Information Provider) ID.
 *
 * Lives on the admin Settings page beside LabModuleSettings /
 * AdmissionModuleSettings / ProcedurePaymentSettings (same card pattern),
 * gated by the page's own isAdmin check — a non-admin never reaches this
 * card. Single DB-backed value, read/written by BOTH directions of the live
 * ABDM resolver (outbound publish + inbound webhook/callback routing — see
 * app/abdm/domain/hip/registration/facility.py on the backend). Empty (the
 * default) means unset. A HIP ID always saves; the backend only reports
 * whether ABDM could verify it (VERIFIED / PENDING + reason). A name that
 * came from ABDM is read-only.
 */
export default function HipSettings() {
  const { ready, enabled } = useAbdmEnabled();
  const abdmOff = ready && !enabled; // R232: an OFF org may still save its HIP ID; no ABDM re-check offered
  const abdmHipId = useOrgSettingsStore((s) => s.abdmHipId);
  const abdmRegisteredName = useOrgSettingsStore((s) => s.abdmRegisteredName);
  const hipLoaded = useOrgSettingsStore((s) => s.hipLoaded);
  const fetchHipSettings = useOrgSettingsStore((s) => s.fetchHipSettings);
  const saveHipSettings = useOrgSettingsStore((s) => s.saveHipSettings);
  const verifyHipSettings = useOrgSettingsStore((s) => s.verifyHipSettings);
  const nameSource = useOrgSettingsStore((s) => s.abdmNameSource);
  const hipStatus = useOrgSettingsStore((s) => s.abdmHipStatus);
  const hipStatusReason = useOrgSettingsStore((s) => s.abdmHipStatusReason);

  const [value, setValue] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!hipLoaded) fetchHipSettings();
  }, [hipLoaded, fetchHipSettings]);

  // Seed the input from the cached value once it loads.
  useEffect(() => {
    if (hipLoaded) {
      setValue(abdmHipId || '');
      setName(abdmRegisteredName || '');
    }
  }, [hipLoaded, abdmHipId, abdmRegisteredName]);

  const trimmed = value.trim();
  const trimmedName = name.trim();
  const hipDirty = trimmed !== (abdmHipId || '');
  const nameLocked = nameSource === 'ABDM';
  const nameDirty = !nameLocked && trimmedName !== (abdmRegisteredName || '');
  const dirty = hipDirty || nameDirty;

  const [checking, setChecking] = useState(false);
  const [stillPending, setStillPending] = useState(false);
  const checkingRef = useRef(false);

  const onRecheck = async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    setChecking(true);
    setErr(null);
    setStillPending(false);
    try {
      const data = await verifyHipSettings();
      setStillPending(data?.abdm_hip_status === 'PENDING');
    } catch (e) {
      const d = e?.response?.data;
      const detail = d?.detail;
      setErr((typeof detail === 'string' ? detail : detail?.message) || d?.message || SETTINGS_COPY.recheckError);
    } finally {
      checkingRef.current = false;
      setChecking(false);
    }
  };

  const savedTimer = useRef(null);
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const onSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (busy || !dirty) return;
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await saveHipSettings({
        ...(hipDirty ? { hipId: trimmed } : {}),
        ...(nameDirty ? { registeredName: trimmedName } : {}),
      });
      // Counter QR text embeds the HIP ID - re-read it (only if the counters card already loaded it).
      if (hipDirty && useScanCountersStore.getState().loaded) useScanCountersStore.getState().fetchCounters({ force: true });
      setSaved(true);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      const d = e?.response?.data;
      const detail = d?.detail;
      setErr(
        (typeof detail === 'string' ? detail : detail?.message) ||
        d?.message || e?.message || SETTINGS_COPY.saveError,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 mt-5 shadow-sm">
      <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">{SETTINGS_COPY.hipTitle}</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
        {SETTINGS_COPY.hipText}
      </p>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
        {SETTINGS_COPY.hipNameText}
      </p>

      {abdmOff && (
        <p className="text-sm text-slate-700 dark:text-slate-200 mt-2 max-w-xl" data-testid="hip-abdm-off-note">
          {SETTINGS_COPY.hipAbdmOff}
        </p>
      )}

      <form onSubmit={onSave} className="flex items-end gap-2 mt-3 flex-wrap" data-testid="hip-form">
        <div>
          <label htmlFor="hip-id" className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
            HIP ID
          </label>
          <input
            id="hip-id"
            type="text"
            value={value}
            disabled={busy}
            onChange={(e) => { setValue(e.target.value); setSaved(false); }}
            className="w-64 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60"
            data-testid="hip-id-input"
          />
        </div>
        <div>
          <label htmlFor="hip-registered-name" className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
            {SETTINGS_COPY.hipNameLabel}
            {nameLocked && (
              <span
                className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-[10px] font-semibold"
                data-testid="hip-name-from-abdm"
              >
                {SETTINGS_COPY.fromAbdm}
              </span>
            )}
          </label>
          <input
            id="hip-registered-name"
            type="text"
            value={name}
            maxLength={200}
            disabled={busy}
            readOnly={nameLocked}
            title={nameLocked ? SETTINGS_COPY.nameFromAbdmHint : undefined}
            onChange={(e) => { setName(e.target.value); setSaved(false); }}
            className={`w-64 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60 ${
              nameLocked ? 'bg-slate-100 dark:bg-slate-700' : 'bg-white dark:bg-slate-900'
            }`}
            data-testid="hip-registered-name-input"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !dirty}
          title={!dirty && !busy ? SETTINGS_COPY.saveDisabledHint : undefined}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
          data-testid="hip-id-save"
        >
          {busy && (
            <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" aria-hidden="true" />
          )}
          {busy ? SETTINGS_COPY.savingLabel : saved && !dirty ? SETTINGS_COPY.savedLabel : SETTINGS_COPY.saveLabel}
        </button>
      </form>

      {hipStatus === 'VERIFIED' && (
        <div className="mt-2" data-testid="hip-status-verified">
          <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-100 text-xs font-semibold">
            {SETTINGS_COPY.statusVerified}
          </span>
        </div>
      )}
      {hipStatus === 'PENDING' && (
        <div className="mt-2" data-testid="hip-status-pending">
          <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100 text-xs font-semibold">
            {SETTINGS_COPY.statusPending}
          </span>
          {!abdmOff && (
          <button
            type="button"
            onClick={onRecheck}
            disabled={checking || busy}
            className="ml-2 px-2.5 py-0.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
            data-testid="hip-recheck"
          >
            {checking ? SETTINGS_COPY.rechecking : SETTINGS_COPY.recheckLabel}
          </button>
          )}
          {stillPending && !checking && (
            <span className="ml-2 text-xs text-slate-600 dark:text-slate-300" data-testid="hip-still-pending">
              {SETTINGS_COPY.stillPending}
            </span>
          )}
          {SETTINGS_COPY.statusReasons[hipStatusReason] && (
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1" data-testid="hip-status-reason">
              {SETTINGS_COPY.statusReasons[hipStatusReason]}
            </p>
          )}
        </div>
      )}

      {err && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2" role="alert">{err}</div>}
    </div>
  );
}
